(() => {
  class Categories {
    selectors = {
      tab: '.js-contact-tab',
      group: '.js-contact-group'
    };

    classes = {
      active: 'active'
    };

    constructor(section) {
      this.section = section;
      this.tabs = [...section.querySelectorAll(this.selectors.tab)];
      this.groups = [...section.querySelectorAll(this.selectors.group)];
      this.allTab = this.tabs.find((tab) => !tab.dataset.target);

      if (!this.tabs.length || !this.groups.length) return;

      this.init();
    }

    init() {
      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => {
          this.changeCategory(tab);
        });
      });
    }

    changeCategory(tab) {
      const target = tab.dataset.target;
      this.setActiveTab(tab);
      target ? this.showGroup(target) : this.showAllGroups();
    }

    setActiveTab(activeTab) {
      this.tabs.forEach((tab) => {
        tab.classList.remove(this.classes.active);
        tab.disabled = false;
      });

      activeTab.classList.add(this.classes.active);
      activeTab.disabled = true;
    }

    showGroup(targetId) {
      this.groups.forEach((group) => {
        group.classList.toggle(this.classes.active, group.id === targetId);
      });
    }

    showAllGroups() {
      this.groups.forEach((group) => {
        group.classList.add(this.classes.active);
      });
    }

    activateAll() {
      if (!this.allTab || this.allTab.classList.contains(this.classes.active)) return;
      this.setActiveTab(this.allTab);
      this.showAllGroups();
    }
  }

  class Accordions {
    selectors = {
      item: '.js-contact-item',
      control: '.contact-benchmark__item-control',
      content: '.js-contact-item-content',
      contentInner: '.js-contact-item-content-inner',
      deferredIframe: '.js-contact-deferred-iframe'
    };

    classes = {
      active: 'active'
    };

    constructor(section) {
      this.section = section;
      this.accordions = [];
      this.init();
    }

    init() {
      this.section.querySelectorAll(this.selectors.item).forEach((element) => {
        this.setupAccordion(element);
      });
    }

    setupAccordion(element) {
      const control = element.querySelector(this.selectors.control);
      const content = element.querySelector(this.selectors.content);
      const contentInner = element.querySelector(this.selectors.contentInner);

      if (!control || !content || !contentInner) return;

      const accordion = {
        element,
        control,
        content,
        contentInner,
        isOpen: element.dataset.accordionOpen === 'true',
        timeout: null
      };

      this.accordions.push(accordion);

      control.addEventListener('click', () => this.toggle(accordion));

      if (accordion.isOpen) {
        this.hydrateDeferredIframes(accordion);
        accordion.element.classList.add(this.classes.active);
        accordion.content.style.height = 'auto';
      }
    }

    // iframes (e.g. youtube embeds) sit in <template> wrappers so the browser
    // doesn't fetch the embed bundle until the user expands the accordion
    hydrateDeferredIframes(accordion) {
      accordion.element.querySelectorAll(this.selectors.deferredIframe).forEach((wrapper) => {
        const template = wrapper.querySelector('template');
        if (!template) return;
        wrapper.appendChild(template.content.cloneNode(true));
        template.remove();
      });
    }

    toggle(accordion) {
      accordion.isOpen ? this.close(accordion) : this.open(accordion);
    }

    open(accordion) {
      if (accordion.isOpen) return;

      this.hydrateDeferredIframes(accordion);
      clearTimeout(accordion.timeout);
      accordion.isOpen = true;
      accordion.element.classList.add(this.classes.active);
      accordion.control.setAttribute('aria-expanded', 'true');
      accordion.content.style.height = `${accordion.contentInner.offsetHeight}px`;

      accordion.timeout = setTimeout(() => {
        accordion.content.style.height = 'auto';
      }, 300);
    }

    close(accordion) {
      if (!accordion.isOpen) return;

      clearTimeout(accordion.timeout);
      accordion.isOpen = false;
      accordion.element.classList.remove(this.classes.active);
      accordion.control.setAttribute('aria-expanded', 'false');
      accordion.content.style.height = `${accordion.contentInner.offsetHeight}px`;
      accordion.timeout = setTimeout(() => {
        accordion.content.style.height = 0;
      }, 0);
    }
  }

  class Search {
    selectors = {
      input: '.js-contact-search-input',
      clear: '.js-contact-search-clear',
      item: '.js-contact-item',
      itemQuestion: '.contact-benchmark__item-question',
      itemContent: '.js-contact-item-content-inner',
      group: '.js-contact-group',
      noResults: '.js-contact-no-results'
    };

    classes = {
      itemHidden: 'is-search-hidden',
      groupEmpty: 'is-search-empty',
      noResultsVisible: 'is-visible'
    };

    constructor(section, categories) {
      this.section = section;
      this.categories = categories;
      this.input = section.querySelector(this.selectors.input);

      if (!this.input) return;

      this.clearButton = section.querySelector(this.selectors.clear);
      this.noResults = section.querySelector(this.selectors.noResults);
      this.groups = [...section.querySelectorAll(this.selectors.group)];
      this.items = [...section.querySelectorAll(this.selectors.item)].map((element) => ({
        element,
        group: element.closest(this.selectors.group),
        searchText: this.buildSearchText(element)
      }));
      this.lastQuery = null;

      this.bind();
    }

    buildSearchText(element) {
      const question = element.querySelector(this.selectors.itemQuestion)?.textContent || '';
      const content = element.querySelector(this.selectors.itemContent)?.textContent || '';
      return `${question} ${content}`.toLowerCase();
    }

    bind() {
      this.input.addEventListener('input', () => this.handleInput());
      this.clearButton?.addEventListener('click', () => this.clear());
    }

    handleInput() {
      const query = this.input.value.trim().toLowerCase();
      if (this.clearButton) this.clearButton.hidden = query === '';
      if (query === this.lastQuery) return;
      this.lastQuery = query;

      if (query === '') {
        this.exitSearchMode();
        return;
      }

      this.enterSearchMode();
      this.applyFilter(query);
    }

    // search spans every category, so the active filter must be reset to All
    // before applying the search-text filter — otherwise matches in inactive
    // categories would still be hidden by the category filter
    enterSearchMode() {
      this.categories?.activateAll();
    }

    applyFilter(query) {
      const matchedGroups = new Set();
      this.items.forEach(({ element, group, searchText }) => {
        const matches = searchText.includes(query);
        element.classList.toggle(this.classes.itemHidden, !matches);
        if (matches && group) matchedGroups.add(group);
      });

      this.groups.forEach((group) => {
        group.classList.toggle(this.classes.groupEmpty, !matchedGroups.has(group));
      });

      this.noResults?.classList.toggle(this.classes.noResultsVisible, matchedGroups.size === 0);
    }

    exitSearchMode() {
      this.items.forEach(({ element }) => element.classList.remove(this.classes.itemHidden));
      this.groups.forEach((group) => group.classList.remove(this.classes.groupEmpty));
      this.noResults?.classList.remove(this.classes.noResultsVisible);
    }

    clear() {
      this.input.value = '';
      this.handleInput();
      this.input.focus();
    }
  }

  if (!customElements.get('contact-benchmark')) {
    class ContactBenchmark extends HTMLElement {
      components = {};

      connectedCallback() {
        this.components.accordions = new Accordions(this);
        this.components.categories = new Categories(this);
        this.components.search = new Search(this, this.components.categories);

        this.querySelectorAll('.js-contact-chat-open').forEach((button) => {
          button.addEventListener('click', () => window.GolfDaddyChat?.open());
        });
      }
    }

    customElements.define('contact-benchmark', ContactBenchmark);
  }
})();
